import { useLatest, usePrevious } from '@solinkcorp/solink-components';
import {
  canSeeAspenWeb,
  canViewEventsPage,
  userHasFeature,
  type UserFeature,
  type UserWithFeatures,
} from '@solinkcorp/user-features';
import { auth } from '@solinkcorp/vd-common';
import { useEffect } from 'react';
import { useAuthenticationState, useLogoutHandlers } from './internal/AuthenticationContext';
import { type ImpersonatorSession, type LogoutHandler, type UserSession } from './types';

/**
 * Returns the current user session.
 */
export function useUserSession(): UserSession;
export function useUserSession(): null;
export function useUserSession(): UserSession | null {
  const [authState] = useAuthenticationState();

  if (authState.isPublicSession) {
    return null; // There is no user session in a public session.
  }

  if (!authState.userSession) {
    // You should never see this error in a child of AuthenticationProvider,
    // since it won't even render children unless a user is logged in.
    throw new Error('No user currently logged in.');
  }

  return authState.userSession;
}

/**
 * If the logged in user is being impersonated, this returns the session for the
 * user that's doing the impersonating.
 */
export function useImpersonator(): ImpersonatorSession | undefined {
  const [authState] = useAuthenticationState();
  return authState.impersonator;
}

/**
 * This hook calls the specified function whenever the currently logged in user
 * changes, or their session refreshes.
 *
 * Much like `useEffect()`, the passed in function can return a "cleanup" function
 * which will be run when the component dismounts, or will be run immediately
 * before the session changes again.
 */
export function useOnUserSessionChange(
  fn: (
    user: UserSession,
    impersonator: ImpersonatorSession | undefined,
    prevUser: UserSession | undefined,
  ) => (() => void) | undefined,
): void {
  const user = useUserSession();
  const impersonator = useImpersonator();
  const latestImpersonator = useLatest(impersonator);
  const latestFn = useLatest(fn);

  const prevUser = usePrevious(user);

  useEffect(() => {
    return latestFn.current(user, latestImpersonator.current, prevUser);
  }, [user, prevUser, latestImpersonator, latestFn]);
}

/**
 * This registers a function that will be called exactly once either when the
 * component dismounts or immediately before the user logs out.
 */
export function useOnLogout(handler: LogoutHandler): void {
  const logoutHandlers = useLogoutHandlers();

  // Add a ref to the handler to logoutHandlers, so that if the handler changes,
  // we can replace it in constant-time.
  const handlerRef = useLatest(handler);

  useEffect(() => {
    logoutHandlers.push(handlerRef);
    return () => {
      const handlerIndex = logoutHandlers.indexOf(handlerRef);
      if (handlerIndex !== -1) {
        logoutHandlers.splice(handlerIndex, 1);
      }
    };
  }, [handlerRef, logoutHandlers]);
}

/**
 * Returns whether the current user has the given feature flag.
 */
export function useUserHasFeature(feature: UserFeature): boolean {
  const user = useUserSession();
  return userHasFeature(user, feature);
}

/**
 * Returns whether the current user satisfies the given predicate.
 *
 * A public session has no user, so the predicate does not run and the result is
 * false. This matches `userHasFeature`, which treats a missing user as a user
 * with no features.
 */
export function useUserConditions(predicate: (user: UserWithFeatures) => boolean): boolean {
  const user: UserSession | null = useUserSession();
  return user ? predicate(user) : false;
}

/**
 * Returns whether the current user may access the Events page — the shared gate
 * for the Data Explorer "View on Events Page" hop. See {@link canViewEventsPage}.
 */
export function useCanViewEventsPage(): boolean {
  return useUserConditions(canViewEventsPage);
}

/**
 * Returns whether the current user sees the Aspen web app.
 * A Solink user with the aspenWeb feature stays on.
 * A user who is not a Solink user stays off.
 * For every other Solink user, the organization policy and the personal opt-in decide.
 * See {@link canSeeAspenWeb}.
 */
export function useCanSeeAspenWeb(): boolean {
  return useUserConditions(canSeeAspenWeb);
}

/**
 * Build the user object `canSeeAspenWeb` and `userHasFeature` expect for an impersonator.
 * Include `clientSettings` so the organization rollout policy applies.
 */
export function impersonatorAsUser(
  profile: UserWithFeatures['profile'] & { email: string },
  clientSettings?: UserWithFeatures['clientSettings'],
): UserWithFeatures {
  return {
    type: 'impersonator',
    isSolinkUser: auth.isSolinkEmail(profile.email),
    clientSettings,
    profile,
  };
}

/**
 * Returns whether the impersonator may see the Aspen Web redesign.
 * Use this on routes that render before an impersonated user session exists.
 * `useCanSeeAspenWeb` reads `userSession` and throws when that session is absent.
 */
export function useImpersonatorCanSeeAspenWeb(): boolean {
  const impersonator = useImpersonator();
  if (!impersonator) {
    return false;
  }

  return canSeeAspenWeb(impersonatorAsUser(impersonator.profile, impersonator.clientSettings));
}
